// Skills v2 — "Add to an agent" modal. The connective tissue that turns the
// Gallery / Author surfaces from browse-only into a real adopt flow: pick an
// agent, attach the skill (table-less general instance, same path as the
// on-agent "Add a skill manually"), and hand the agent id back so the caller can
// land the user on that agent's Overview. No dead-ends.
import { useEffect, useState } from "react";
import { COLORS } from "../../theme/colors";
import { MATERIALS } from "../../theme/materials";
import { skillsApi, agentApi } from "../../api/client";

const BORDER = COLORS.border || "#e2e5eb";
const MUTED = COLORS.textMuted || "#6b7280";
const DIM = COLORS.textDim || "#9090a8";

function unwrapArray(res) {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  return [];
}

export default function AttachToAgentModal({ skillName, displayName, onClose, onAttached }) {
  const [agents, setAgents] = useState(null);
  const [agentId, setAgentId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = unwrapArray(await agentApi.list()).filter((a) => a && !a.metadata_json?.ephemeral);
        if (!alive) return;
        setAgents(list);
        if (list[0]) setAgentId(list[0].id);
      } catch {
        if (alive) { setAgents([]); setError("Couldn't load your agents."); }
      }
    })();
    return () => { alive = false; };
  }, []);

  const attach = async () => {
    if (!agentId) return;
    setBusy(true); setError(null);
    try {
      const res = await skillsApi.attachGeneral(agentId, skillName);
      if (res && res.success === false) throw new Error(res.error || "Attach failed");
      const name = agents?.find((a) => a.id === agentId)?.name || agentId;
      onAttached(agentId, name);
    } catch (e) {
      const msg = (e && e.message) || "";
      setError(/412|connect|integration/i.test(msg)
        ? "This skill needs a connection first — add it, then connect it on the agent's Wiring tab."
        : "Couldn't add it just now. Try another agent, or add it from the agent's Overview.");
    } finally { setBusy(false); }
  };

  return (
    <div
      role="dialog" aria-label={`Add ${displayName || skillName} to an agent`}
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(4,6,15,0.55)", zIndex: 120, display: "grid", placeItems: "center", padding: 20 }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "min(460px, 100%)", ...MATERIALS.thickGlass(), borderRadius: 16,
        border: `1px solid ${BORDER}`, boxShadow: MATERIALS.spatialShadow(), padding: "18px 20px",
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>
          Add <span style={{ color: COLORS.accentText || COLORS.accent }}>{displayName || skillName}</span> to an agent
        </div>
        <div style={{ fontSize: 12.5, color: MUTED, marginTop: 4, lineHeight: 1.5 }}>
          Pick the agent that needs this skill — we'll attach it and take you to that agent.
        </div>

        <div style={{ marginTop: 14 }}>
          <label style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: DIM }}>Agent</label>
          {agents === null ? (
            <div style={{ fontSize: 12.5, color: DIM, marginTop: 6 }}>Loading agents…</div>
          ) : agents.length === 0 ? (
            <div style={{ fontSize: 12.5, color: DIM, marginTop: 6 }}>No agents yet — create one first.</div>
          ) : (
            <select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              style={{
                display: "block", width: "100%", marginTop: 6, boxSizing: "border-box",
                font: "inherit", fontSize: 13, fontWeight: 600, color: COLORS.text,
                background: COLORS.surface, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "9px 11px",
              }}
            >
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
            </select>
          )}
        </div>

        {error && <div style={{ fontSize: 12, color: COLORS.orange || "#d97706", marginTop: 10, lineHeight: 1.5 }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
          <button type="button" onClick={onClose} style={{
            font: "inherit", fontSize: 13, fontWeight: 600, color: MUTED, background: COLORS.surface,
            border: `1px solid ${BORDER}`, borderRadius: 9, padding: "8px 14px", cursor: "pointer",
          }}>Cancel</button>
          <button type="button" onClick={attach} disabled={busy || !agentId} style={{
            font: "inherit", fontSize: 13, fontWeight: 700, color: "#fff",
            background: COLORS.accent, border: "none", borderRadius: 9, padding: "8px 16px",
            cursor: busy || !agentId ? "default" : "pointer", opacity: busy || !agentId ? 0.6 : 1,
            boxShadow: `0 0 16px ${COLORS.accentGlow}`,
          }}>{busy ? "Adding…" : "＋ Add to agent"}</button>
        </div>
      </div>
    </div>
  );
}
